import { useCallback, useEffect, useRef, type ReactNode } from 'react'

import { parseCursorPage } from '@/shared/api/paging'
import { useApiClient } from '@/shared/api/use-api-client'
import { ActorNamesProvider, type ActorName, type ActorNameLookup } from '@/shared/details'

import { apiClientNamesApi, apiClientPath, parseApiClientName, readApiClientsPermission } from './api-client'

/** The most ids the API names in one call. */
const batchSize = 100

/**
 * Registers Channels' name lookup for the `ApiClient` actor type, so every "Created by" line names
 * the client that made a change, deleted ones included, linked for a viewer who may read clients
 * unless it is deleted.
 */
export function ApiClientNamesProvider(props: { readonly children: ReactNode }) {
  const client = useApiClient()
  // Kept current without changing the lookup, which would look every name up again: the client
  // changes when sign-in settles.
  const api = useRef(client)
  useEffect(() => {
    api.current = client
  }, [client])

  const lookup = useCallback<ActorNameLookup>(async (ids) => {
    const batches: (readonly string[])[] = []
    for (let start = 0; start < ids.length; start += batchSize) {
      batches.push(ids.slice(start, start + batchSize))
    }
    const pages = await Promise.all(
      batches.map(async (batch) => {
        const query = new URLSearchParams({ ids: batch.join(',') })
        return parseCursorPage(await api.current.get(`${apiClientNamesApi}?${query.toString()}`), parseApiClientName)
      }),
    )
    return new Map<string, ActorName>(
      pages.flatMap((page) =>
        page.data.map((name) => [name.id, name.isDeleted ? { name: name.name } : { name: name.name, path: apiClientPath(name.id), permission: readApiClientsPermission }] as const),
      ),
    )
  }, [])

  return (
    <ActorNamesProvider type="apiClient" lookup={lookup}>
      {props.children}
    </ActorNamesProvider>
  )
}
